import React, { Fragment, useEffect, useState } from "react";
import { BreadCrumb } from "../../Layout/Common/breadCrumb";
import { PAGES } from "../../routeConfig";
import { axiosInstance } from "../../axios/axiosInstance";
import { Link, useNavigate, useParams } from "react-router-dom";
import { isValidArray } from "../../Layout/Common/common";
import { trackingStatus } from "../../constants";
import { OrderStatusStepper } from "./orderStatusStepper";
import "./trackOrder.css";
import { Spinner } from "../../Layout/Common/spinner";
import { SuspenseLoader } from "../../Layout/Common/loader";

export const TrackOrderMain = ({ orderRef }) => {
  const { page, trackOrderNumber } = useParams();
  const [orderNumber, setOrderNumber] = useState("");
  const [orderData, setOrderData] = useState(null);
  const [trackingData, setTrackingData] = useState(trackingStatus);
  // Start in the loading state when we have an order to fetch, so the very
  // first paint shows the skeleton rather than a flash of "No Data Found".
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  useEffect(() => {
    if (!trackOrderNumber || !orderRef) {
      setTrackingData(trackingStatus);
      setOrderData(null);
      setLoading(true);
      fetchData(trackOrderNumber ?? orderRef);
    } else {
      setLoading(false);
      setOrderData(null);
    }
  }, [trackOrderNumber, orderRef]);

  const mergeTrackingStatus = (apiData) => {
    if (!isValidArray(apiData)) return trackingStatus;

    let lastCompletedIndex = -1;
    trackingStatus.forEach((item, index) => {
      if (apiData.some((res) => res.orderStatus === item.orderStatus)) {
        lastCompletedIndex = index;
      }
    });

    return trackingStatus.map((item, index) => {
      const matched = apiData.find((res) => res.orderStatus === item.orderStatus);
      return {
        ...item,
        isCompleted: index <= lastCompletedIndex,
        completedDate: matched ? matched.completedDate : item.completedDate,
      };
    });
  };

  const fetchData = async (trackOrderNumber) => {
    try {
      setLoading(true);
      setError("");
      const response = await axiosInstance.post("MyOrderHistory/getOrderTracking?orderId=" + trackOrderNumber);
      const data = await response.data;
      setTrackingData(mergeTrackingStatus(data.status));
      setOrderData(data.detail);
    } catch (err) {
      setError("Failed to fetch tracking information");
    }
    setLoading(false);
  };

  return (
    <Fragment>
      {trackOrderNumber && (
        <BreadCrumb
          data={[
            { name: "My Account", url: PAGES.MYACCOUNT },
            { name: "Order History", url: PAGES.ORDERHISTORY },
            { name: "Order Tracking", url: "" },
          ]}
        />
      )}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "100vh" }}>
          <Spinner />
        </div>
      ) : orderData ? (
        <section className="ordercomplete-trackingSection">
          <div className="container-fluid container-lg">
            <div className="row">
              <div className="col-md-12">
                <div className="orderTracking-title">
                  <h1>Order Tracking</h1>
                  <div className="order-header-card">
                    <div>
                      <div className="order-id-label">
                        Order ID: <span>#{orderData.orderId}</span>
                      </div>
                      <div className="order-sub-text">
                        You can check the status of your order using the tracking details provided here.
                      </div>
                    </div>
                    <div className="wesFlyerLink border-link">
                      <Link
                        onClick={(e) => {
                          e.preventDefault();
                          navigate(-1);
                        }}
                      >
                        Back to My Account
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-12">
                {/* Order status stepper */}
                <OrderStatusStepper steps={trackingData} />
              </div>
              {/* Courier info */}
              <div className="col-md-12">
                <div className="courier-info">
                  <p>
                    <span className="label">Courier Name :</span> {orderData.shippingCompany}
                  </p>
                  <p>Tracking Number : #{orderData.trackingNumber}</p>
                </div>
              </div>
              {/* Courier info */}
              {/* Tracking Timeline */}
              <div className="col-md-12">
                <div className="trackingTimeline">
                  <ul className="tracking-timeline">
                    <li className="timeline-item">
                      <div className="timeline-date-badge">
                        <div className="day">01</div>
                        <div className="month">Feb</div>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-event">Order Confirmed</div>
                        <div className="timeline-meta">Saturday 9:00 AM, Location - Sydney</div>
                      </div>
                    </li>
                    <li className="timeline-item">
                      <div className="timeline-date-badge">
                        <div className="day">03</div>
                        <div className="month">Feb</div>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-event">Shipment is out for delivery</div>
                        <div className="timeline-meta">Monday 11:30 AM, Location - Sydney</div>
                      </div>
                    </li>
                    <li className="timeline-item">
                      <div className="timeline-date-badge">
                        <div className="day">03</div>
                        <div className="month">Feb</div>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-event">In-Transit to Melbourne hub</div>
                        <div className="timeline-meta">Monday 11:30 AM, Location - Sydney</div>
                      </div>
                    </li>
                    <li className="timeline-item">
                      <div className="timeline-date-badge">
                        <div className="day">03</div>
                        <div className="month">Feb</div>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-event">Shipment Pickup Complete</div>
                        <div className="timeline-meta">Monday 11:30 AM, Location - Sydney</div>
                      </div>
                    </li>
                    <li className="timeline-item">
                      <div className="timeline-date-badge">
                        <div className="day">04</div>
                        <div className="month">Feb</div>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-event">Your Item has been picked up by courier partner</div>
                        <div className="timeline-meta">Tuesday 5:45 PM, Location - Melbourne</div>
                      </div>
                    </li>
                    <li className="timeline-item">
                      <div className="timeline-date-badge">
                        <div className="day">04</div>
                        <div className="month">Feb</div>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-event">Item Packed in Dispatch nearest Warehouse to you</div>
                        <div className="timeline-meta">Tuesday 12:15 PM, Location - Melbourne</div>
                      </div>
                    </li>
                    <li className="timeline-item">
                      <div className="timeline-date-badge">
                        <div className="day">05</div>
                        <div className="month">Feb</div>
                      </div>
                      <div className="timeline-content">
                        <div className="timeline-event">Delivery Expected by Thu, 05 Feb 2026</div>
                        <div className="timeline-meta">Item yet to be delivered</div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
              {/* Tracking Timeline */}
            </div>
          </div>
        </section>
      ) : (
        <section className="ordercomplete-trackingSection">
          <div className="container-fluid container-lg">
            <div className="row">
              <div className="col-md-12 text-center">No Data Found.</div>
            </div>
          </div>
        </section>
      )}
    </Fragment>
  );
};
